import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useAnalytics } from '@Context/AnalyticsContext';
import { ApexOptions } from 'apexcharts';

const LEGEND_COLORS = ['#3B82F6', '#10B981', '#F59E0B'];

const formatTokenValue = (value: number): string => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
};

const AnalyticsLineChart: React.FC = () => {
  const { timeSeriesData, loading } = useAnalytics();

  const series = useMemo(
    () => [
      {
        name: 'Tokens',
        type: 'line' as const,
        data: timeSeriesData.map((item) => ({
          x: item.timestamp,
          y: item.tokens,
        })),
      },
      {
        name: 'Cost',
        type: 'line' as const,
        yAxisIndex: 1,
        data: timeSeriesData.map((item) => ({
          x: item.timestamp,
          y: item.cost,
        })),
      },
    ],
    [timeSeriesData]
  );

  const chartOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: false,
            reset: true,
          },
        },
        animations: { enabled: true },
      },
      stroke: { curve: 'smooth', width: 3 },
      colors: LEGEND_COLORS,
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'MMM dd',
          style: { fontSize: '12px', colors: '#6B7280' },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: [
        {
          title: {
            text: 'Tokens',
            style: { color: '#3B82F6', fontSize: '12px', fontWeight: 600 },
          },
          labels: {
            style: { colors: '#3B82F6', fontSize: '12px' },
            formatter: formatTokenValue,
          },
        },
        {
          opposite: true,
          title: {
            text: 'Cost ($)',
            style: { color: '#10B981', fontSize: '12px', fontWeight: 600 },
          },
          labels: {
            style: { colors: '#10B981', fontSize: '12px' },
            formatter: (value) => `$${value.toFixed(4)}`,
          },
        },
      ],
      grid: { borderColor: '#E5E7EB', strokeDashArray: 3 },
      legend: { show: false },
      tooltip: {
        shared: true,
        intersect: false,
        y: [
          { formatter: (value) => `${value.toLocaleString()} tokens` },
          { formatter: (value) => `$${value.toFixed(4)}` },
          { formatter: (value) => `${value} requests` },
        ],
      },
      markers: {
        size: 4,
        strokeWidth: 2,
        strokeColors: '#fff',
        hover: { size: 6 },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.25,
          gradientToColors: undefined,
          inverseColors: false,
          opacityFrom: 0.85,
          opacityTo: 0.85,
        },
      },
    }),
    []
  );

  if (loading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="animate-pulse">
          <div className="mb-4 h-4 w-1/4 rounded bg-gray-300"></div>
          <div className="h-96 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  if (timeSeriesData.length === 0) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="py-12 text-center">
          <div className="mb-2 text-lg text-gray-400">No Data Available</div>
          <div className="text-sm text-gray-500">
            No analytics data found for the selected filters.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Usage Over Time</h3>
        <p className="text-sm text-gray-600">Token usage and cost overtime</p>
      </div>
      <div className="mb-4">
        <div
          className="flex items-center gap-4 overflow-x-auto whitespace-nowrap pb-1"
          role="list"
          aria-label="Chart legend"
        >
          {series.map((s, idx) => (
            <div
              key={s.name}
              role="listitem"
              className="flex items-center gap-2 text-sm text-gray-700"
              style={{ flex: '0 0 auto' }}
            >
              <span
                className="inline-block h-3 w-3 rounded"
                style={{ backgroundColor: LEGEND_COLORS[idx] }}
                aria-hidden
              />
              <span className="font-medium">{s.name}</span>
            </div>
          ))}
        </div>
      </div>

      <ReactApexChart
        options={chartOptions}
        series={series}
        type="line"
        height={400}
      />
    </div>
  );
};

export default AnalyticsLineChart;
